import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TextField, Drawer, Box, Button, Typography, Card } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import SuperbillTasksList from 'components/Analyses/SuperbillTasks/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuperbillAnalyses from 'hooks/useSuperbillAnalyses';
import useSuperbillTasks from 'hooks/useSuperbillTasks';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { superbillAnalysesSelectors } from 'features/superbillAnalyses';

const SuperbillAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { superbill_analysis_id } = useParams();
  const { getSuperbillAnalysis, clearLocalActiveSuperbillAnalysis } = useSuperbillAnalyses();
  const { getSuperbillTasks, clearLocalSuperbillTasks } = useSuperbillTasks();
  const { getSuperbillAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const superbillAnalysisFetching = useCustomSelector(superbillAnalysesSelectors.superbillAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchSuperbillAnalysis = useCallback((id) => {
    setOpen(true);
    getSuperbillAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { superbill_analysis } } = payload;
        handleSetData(superbill_analysis);
      }
    })
  }, [getSuperbillAnalysis]);

  const handleSetData = (superbill_analysis) => {
    setResult(superbill_analysis?.result);
  };

  const handleExiting = () => {
    setResult('');
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl19s`);
    clearLocalActiveSuperbillAnalysis();
    clearLocalSuperbillTasks();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchSuperbillTasks = useCallback(() => {
    getSuperbillTasks({ id: superbill_analysis_id, offset: 0 });
  }, [getSuperbillTasks, superbill_analysis_id]);

  const fetchSuperbillAnalysesEvents = useCallback((offset, superbill_analysis_id) => {
    getSuperbillAnalysesEvents({ offset, superbill_analysis_id })
  }, [ getSuperbillAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchSuperbillAnalysesEvents(0, superbill_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!superbill_analysis_id) {
      fetchSuperbillAnalysis(superbill_analysis_id);
      fetchSuperbillTasks();
    }
  }, [superbill_analysis_id, fetchSuperbillAnalysis, fetchSuperbillTasks]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { superbillAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {superbill_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={superbill_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchSuperbillAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
          <Card sx={{ p: 2, mt: 3 }}>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Tasks</Typography>
            <SuperbillTasksList superbillAnalysisId={superbill_analysis_id} />
        </Card>
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SuperbillAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);